import { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { useRouter } from 'next/router';
import { AcademyList, AcademyListProps, Container, AcademyHero, M31ProductGrid, Paragraph } from 'ui/components';
import { getAcademySearchResults, getAcademyFilters } from 'application/repositories/academyRepository';
import { AcademySearchResponse } from 'application/adapters/academy/academyAdapter';
import { parseDMYDateTime, formatAcademyDateString } from 'helpers/date';
import { DictionaryContext } from 'application/adapters/context/Dictionary';

// Helper functions for grouping upcoming academy entries by year
const toTime = (s: string) => parseDMYDateTime(s).getTime();
const groupUpcomingByYear = (
	entries: Models.AcademyEntry[],
	startOfToday: number,
	currentYear: number,
): { year: number; items: Models.AcademyEntry[] }[] => {
	const upcoming = entries;

	const map = new Map<number, Models.AcademyEntry[]>();
	for (const e of upcoming) {
		const y = parseDMYDateTime(e.startDateTime).getFullYear();
		if (!map.has(y)) map.set(y, []);
		map.get(y)!.push(e);
	}

	for (const items of map.values()) {
		items.sort((a, b) => toTime(a.startDateTime) - toTime(b.startDateTime));
	}

	const years = Array.from(map.keys()).sort((a, b) => {
		if (a === currentYear && b !== currentYear) return -1;
		if (b === currentYear && a !== currentYear) return 1;
		return a - b;
	});

	return years.map((y) => ({ year: y, items: map.get(y)! }));
};

export const AcademySearchResults = ({ content, culture, localeContext, linkToEntry }) => {
	const router = useRouter();
	const dictionary = useContext(DictionaryContext);

	// Helper to get localized academy type name
	const getLocalizedTypeName = useCallback(
		(typeName: string | undefined): string => {
			if (!typeName) return '';
			const normalizedTypeName = typeName.trim().replace(/\s+/g, '');
			return dictionary.getValue(`AcademyType.${normalizedTypeName}`, null, typeName.trim());
		},
		[dictionary, router.locale],
	);

	// Helper to get localized language name
	const getLocalizedLanguageName = useCallback(
		(languageName: string | undefined): string => {
			if (!languageName) return '';
			return dictionary.getValue(`AcademyLanguage.${languageName}`, null, languageName);
		},
		[dictionary, router.locale],
	);

	// Helper to format date with current locale (month only)
	const getFormattedDate = useCallback(
		(dateString: string): string => {
			return formatAcademyDateString(dateString, router.locale || 'en');
		},
		[router.locale],
	);

	const academyDictionary = {
		academyFilterAllLangText: dictionary.getValue('Academy.AllLanguagesFilterText', null, 'All languages'),
		academyFilterAllTopicsText: dictionary.getValue('Academy.AllTopicsFilterText', null, 'All topics'),
		academyFilterAllTypesText: dictionary.getValue('Academy.AllTopicsFilterText', null, 'All types'),
		academyFilterTypesLabel: dictionary.getValue('Academy.AcademyFilterTypesLabel', null, 'Academy Types:'),
		academyFilterLanguagesLabel: dictionary.getValue('Academy.AcademyFilterLanguagesLabel', null, 'Language:'),
		academyTypesLabel: dictionary.getValue('Academy.AcademyTypesLabel', null, 'Academy Types:'),
		academyLanguagesLabel: dictionary.getValue('Academy.AcademyLanguagesLabel', null, 'Language:'),
		cardLanguageLabel: dictionary.getValue('Academy.CardLanguageLabel', null, 'Language'),
		cardAddressLabel: dictionary.getValue('Academy.CardAddressLabel', null, 'Address'),
		cardButtonText: dictionary.getValue('Academy.CardButtonText', null, 'Read more'),
		loadMoreButtonText: dictionary.getValue('Academy.LoadMoreButtonText', null, 'Get more results'),
		resultsFoundText: dictionary.getValue('Academy.ResultsFoundText', null, 'results found'),
		cmsErrorMessage: dictionary.getValue('Academy.CmsErrorMessage', null, 'An error occurred while fetching data. Please try again later.'),
	};
	// Show / hide academy hero and grid cards
	const subpagesGridItems = content?.properties?.academyGridItems;
	const showAcademyGridCards =
		subpagesGridItems.heading ||
		subpagesGridItems.description ||
		subpagesGridItems.image ||
		subpagesGridItems.linkButton ||
		subpagesGridItems.linkButtonAriaLabel;
	const academyHeroData = {
		academyTitle: content?.properties?.title,
		text: content?.properties?.description,
		image: content?.properties?.heroImage,
		anchor: content?.properties?.anchor,
	};
	const showHero = academyHeroData.academyTitle || academyHeroData.text || academyHeroData.image || academyHeroData.anchor;

	const [loading, setLoading] = useState(true);
	const [groupedEntries, setGroupedEntries] = useState<AcademyListProps['academyEntries']>([]);
	const [academyFilters, setAcademyFilters] = useState<Models.AcademyFilters | null>(null);
	const [filtersLoading, setFiltersLoading] = useState(true);
	const [selectedType, setSelectedType] = useState('');
	const [selectedLanguage, setSelectedLanguage] = useState('');
	const mounted = useRef(true);

	const pageSize = 4;
	const now = new Date();
	const currentYear = now.getFullYear();
	const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

	const fetchAcademyFilters = useCallback(() => {
		setFiltersLoading(true);
		return getAcademyFilters((router.query.host as string) || '', router.locale || 'en')
			.then((data: Models.AcademyFilters) => {
				if (!mounted.current) return;
				const enhanced = {
					topics: { [academyDictionary?.academyFilterAllTopicsText]: '', ...data?.topics },
					types: { [academyDictionary?.academyFilterAllTypesText]: '', ...data?.types },
					languages: { [academyDictionary?.academyFilterAllLangText]: '', ...data?.languages },
				};
				setAcademyFilters(enhanced);
				setSelectedType((v) => (Object.values(enhanced.types).includes(v) ? v : ''));
				setSelectedLanguage((v) => (Object.values(enhanced.languages).includes(v) ? v : ''));
			})
			.catch(() => {
				if (!mounted.current) return;
				setAcademyFilters(null);
			})
			.finally(() => {
				if (mounted.current) setFiltersLoading(false);
			});
	}, [router.query.host, router.locale]);

	const fetchAcademySearchResults = useCallback(
		(currentType: string, currentLanguage: string) => {
			setLoading(true);
			return getAcademySearchResults(
				{ Topic: '', Type: currentType, Language: currentLanguage, Take: 50, Skip: 0 },
				(router.query.host as string) || '',
				router.locale || 'en',
			)
				.then((data: AcademySearchResponse) => {
					if (!mounted.current) return;

					if (Array.isArray(data?.results)) {
						if (data.results.length > 0) {
							const grouped = groupUpcomingByYear(data.results, startOfToday, currentYear);
							// Transform entries with localized values
							const localized = grouped.map((group) => ({
								year: group.year,
								items: group.items.map((item) => ({
									...item,
									type: item.type
										? {
												...item.type,
												displayName: getLocalizedTypeName(item.type.name),
										  }
										: item.type,
									language: item.language
										? {
												...item.language,
												displayName: getLocalizedLanguageName(item.language.name),
										  }
										: item.language,
									localizedStartDateTime: getFormattedDate(item.startDateTime),
								})),
							}));
							setGroupedEntries(localized);
						} else {
							setGroupedEntries([]);
						}
					}
				})
				.catch(() => {
					if (!mounted.current) return;
					setGroupedEntries([]);
				})
				.finally(() => {
					if (mounted.current) setLoading(false);
				});
		},
		[router.query.host, router.locale],
	);

	useEffect(() => {
		if (!router.isReady) return;
		fetchAcademyFilters();
		return () => {
			mounted.current = false;
		};
	}, [router.isReady, router.locale, fetchAcademyFilters]);

	useEffect(() => {
		if (!router.isReady) return;
		fetchAcademySearchResults(selectedType, selectedLanguage);
	}, [router.isReady, selectedType, selectedLanguage, fetchAcademySearchResults]);

	// Counts for UI
	const totalCount = groupedEntries.reduce((sum, g) => sum + g.items.length, 0);
	const showLoadMore = totalCount > pageSize;

	const handleFilterLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedLanguage(event.target.value);
	};

	const handleFilterTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedType(event.target.value);
	};

	return (
		<>
			{showHero && (
				<AcademyHero
					academyTitle={content?.properties?.title}
					text={content?.properties?.description}
					image={content?.properties?.heroImage}
					anchor={content?.properties?.anchor}
				/>
			)}

			{showAcademyGridCards && <M31ProductGrid isAcademyView gridItems={subpagesGridItems} />}

			<Container width="Standard">
				{content?.properties ? (
					<AcademyList
						{...content?.properties}
						heading={content?.properties.academyListHeading}
						dictionary={academyDictionary}
						loading={loading || filtersLoading}
						academyEntries={groupedEntries}
						pageSize={pageSize}
						totalResults={totalCount}
						showLoadMore={showLoadMore}
						filterOptions={academyFilters}
						selectedAcademyLanguage={selectedLanguage}
						handleFilterLanguageChange={handleFilterLanguageChange}
						selectedAcademyType={selectedType}
						handleFilterTypeChange={handleFilterTypeChange}
						linkToEntry={linkToEntry}
					/>
				) : (
					<Paragraph variant="website" style="lg">
						{academyDictionary.cmsErrorMessage}
					</Paragraph>
				)}
			</Container>
		</>
	);
};
